<template>
  <UIDialog
    :is-open
    :z-index
    title="Remove tag"
    @close="handleClose"
  >
    <UIInputAutocomplete
      v-model="selectedTag"
      :data
      :disabled="loading"
      placeholder="Select tag to remove"
    />
    <UIButtonsGroup>
      <UIButtonClose @click="handleClose" />
      <UIButtonSave
        label="Remove"
        :loading
        :disabled="!selectedTag"
        @click="handleClickRemove"
      />
    </UIButtonsGroup>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { orderBy } from 'lodash'

import {
  LinkedDataTransaction,
  LinkedDataTransactionUpdate,
  ModalClass
} from '@types'

import { useLinkedDataTransactionsTagsStore } from '@/store/linkedData/transactionsTags'

import { UIDialog } from '@ui/modals'
import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { UIInputAutocomplete } from '@ui/fields'

type Props = {
  modal: ModalClass<any>
  items: LinkedDataTransaction[]
  ids: string[]
}

type Emits = {
  process: [data: LinkedDataTransactionUpdate[], callback: () => void]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const linkedDataTransactionsTagsStore = useLinkedDataTransactionsTagsStore()

const loading = ref(false)

const selectedTag = ref<string>()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const data = computed(() =>
  orderBy(
    linkedDataTransactionsTagsStore.getList.filter(item => {
      const selectedTransactionsTags: string[] = []
      props.ids.forEach(id => {
        const transaction = props.items.find(item => item.id === id)
        if (transaction) {
          selectedTransactionsTags.push(
            ...transaction.tags.map(tag => tag.id as string),
          )
        }
      })
      return item.id && selectedTransactionsTags?.includes(item.id)
    }),
    'name',
  ).map(item => ({
    value: item.id?.toString() || '',
    label: item.name,
  })),
)

const handleClose = () => {
  props.modal.close()
}

const handleClickRemove = async () => {
  loading.value = true
  const result = props.ids.reduce((acc, id) => {
    const transaction = props.items.find(item => item.id === id)
    const existingTags = transaction?.tags
    if (
      selectedTag.value &&
      existingTags?.some(tag => tag.id === selectedTag.value)
    ) {
      const tags = existingTags
        .filter(tag => tag.id !== selectedTag.value)
        .map(item => `${item.id}`)
      acc.push({ id, tags })
    }
    return acc
  }, [] as LinkedDataTransactionUpdate[])
  if (!result) {
    loading.value = false
    return
  }
  emit('process', result, () => {
    loading.value = false
  })
  handleClose()
}
</script>
